import {
  buildPermissionContext,
  Resource,
  Action,
  buildPermission,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import React, { useState, useEffect, useCallback, useRef } from "react";

import { deleteEvent, getEventByYear } from "../../api/event";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllClass } from "../../api/pedagogicElement";
import { toast } from "sonner";
import { Link } from "react-router";
import { my_routes } from "../router/all_routes";
import { DatePicker, ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import frFR from "antd/locale/fr_FR";
import CreateEventModal from "./CreateEventModal";
import EditEventModal from "./EditEventModal";
import ViewEventModal from "./ViewEventModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

dayjs.locale("fr");

// ─── Types ────────────────────────────────────────────────────────────────────

export type EventType =
  | "Congé"
  | "Jour Férié"
  | "Examen"
  | "Activité Scolaire"
  | "Dévoir";

export interface ClassOption {
  id: string;
  name: string;
}

export interface EventItem {
  id: string;
  id_userProfil: string;
  id_class: string | string[];
  type: EventType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  all_school: boolean;
  createdAt: string;
  Classes?: { id: string; name: string }[];
  UserProfil?: { firstname: string; lastname: string; email: string };
}

export interface CreateEventPayload {
  id_classes: string[];
  type: EventType;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  all_school: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const EVENT_TYPES: EventType[] = [
  "Congé",
  "Jour Férié",
  "Examen",
  "Activité Scolaire",
  "Dévoir",
];

export const TYPE_COLORS: Record<
  EventType,
  { bg: string; text: string; border: string; soft: string }
> = {
  Congé: { bg: "#10b981", text: "#fff", border: "#059669", soft: "#d1fae5" },
  "Jour Férié": {
    bg: "#6366f1",
    text: "#fff",
    border: "#4f46e5",
    soft: "#e0e7ff",
  },
  Examen: { bg: "#ef4444", text: "#fff", border: "#dc2626", soft: "#fee2e2" },
  "Activité Scolaire": {
    bg: "#06b6d4",
    text: "#fff",
    border: "#0891b2",
    soft: "#cffafe",
  },
  Dévoir: { bg: "#f59e0b", text: "#fff", border: "#d97706", soft: "#fef3c7" },
};

export const TYPE_ICON: Record<EventType, string> = {
  Congé: "fa fa-umbrella",
  "Jour Férié": "fa fa-star",
  Examen: "fa fa-pencil-square",
  "Activité Scolaire": "fa fa-person",
  Dévoir: "fa fa-book",
};

const MONTHS_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export const EMPTY_FORM: CreateEventPayload = {
  id_classes: [],
  type: "Examen",
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  all_school: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
  });
};

export const getDurationDays = (start: string, end: string): number => {
  const diff = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)) + 1);
};

const getEventsForDay = (
  events: EventItem[],
  year: number,
  month: number,
  day: number,
): EventItem[] => {
  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);
  return events.filter((event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return start <= date && end >= date;
  });
};

// ─── Global Styles ────────────────────────────────────────────────────────────

const useStyles = () => {
  useEffect(() => {
    const id = "em-global-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .em-page { background: #f1f5f9; min-height: 100%; }

      /* Header */
      .em-header {
        background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        padding: 1.5rem 2rem;
        margin-bottom: 1.5rem;
        border-radius: 0 0 24px 24px;
      }
      .em-header-title {
        color: #fff;
        font-size: 1.4rem;
        font-weight: 800;
        margin: 0;
        letter-spacing: -0.025em;
      }
      .em-header-sub { color: #94a3b8; font-size: 0.82rem; margin: 0.25rem 0 0; }

      /* Button create */
      .em-btn-create {
        background: #4f46e5;
        color: #fff !important;
        border: none;
        border-radius: 10px;
        padding: 0.55rem 1.1rem;
        font-weight: 700;
        font-size: 0.85rem;
        cursor: pointer;
        transition: all 0.2s;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        text-decoration: none;
      }
      .em-btn-create:hover {
        background: #4338ca;
        transform: translateY(-1px);
        box-shadow: 0 4px 16px rgba(79,70,229,0.45);
        color: #fff !important;
      }

      /* Stat cards */
      .em-stat-card {
        background: #fff;
        border-radius: 14px;
        padding: 0.9rem 1.1rem;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        display: flex;
        align-items: center;
        gap: 0.9rem;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      .em-stat-card:hover { transform: translateY(-2px); box-shadow: 0 5px 16px rgba(0,0,0,0.09); }
      .em-stat-icon {
        width: 44px; height: 44px; border-radius: 11px;
        display: flex; align-items: center; justify-content: center;
        font-size: 1.15rem; flex-shrink: 0;
      }
      .em-stat-value { font-size: 1.45rem; font-weight: 800; color: #1e293b; line-height: 1; }
      .em-stat-label { font-size: 0.73rem; color: #64748b; margin-top: 0.2rem; font-weight: 500; }

      /* Toolbar */
      .em-toolbar {
        background: #fff;
        border-radius: 14px;
        padding: 0.875rem 1.1rem;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        margin-bottom: 1.1rem;
        display: flex;
        align-items: center;
        gap: 0.625rem;
        flex-wrap: wrap;
      }
      .em-search-wrap { position: relative; flex: 1; min-width: 180px; }
      .em-search-wrap > i {
        position: absolute; left: 11px; top: 50%;
        transform: translateY(-50%); color: #94a3b8; pointer-events: none; font-size: 0.8rem;
      }
      .em-search-input {
        width: 100%; border: 1.5px solid #e2e8f0; border-radius: 9px;
        padding: 0.48rem 0.75rem 0.48rem 2rem;
        font-size: 0.84rem; outline: none;
        transition: border-color 0.2s, box-shadow 0.2s;
        color: #1e293b; background: #f8fafc;
      }
      .em-search-input:focus { border-color: #4f46e5; background: #fff; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
      .em-type-filter {
        border: 1.5px solid #e2e8f0; border-radius: 9px;
        padding: 0.48rem 0.75rem; font-size: 0.84rem;
        outline: none; color: #1e293b; background: #f8fafc;
        cursor: pointer; transition: border-color 0.2s; min-width: 150px;
      }
      .em-type-filter:focus { border-color: #4f46e5; background: #fff; }
      .em-btn-reset {
        border: 1.5px solid #e2e8f0; background: #f8fafc; border-radius: 9px;
        padding: 0.48rem 0.8rem; font-size: 0.79rem; color: #64748b; cursor: pointer;
        display: inline-flex; align-items: center; gap: 0.3rem;
        transition: all 0.2s; white-space: nowrap;
      }
      .em-btn-reset:hover { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }

      /* Calendar navigation bar */
      .em-cal-nav {
        background: #fff; border-radius: 14px;
        padding: 0.8rem 1.1rem; border: 1px solid #e2e8f0;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        display: flex; align-items: center;
        justify-content: space-between; margin-bottom: 1rem;
        flex-wrap: wrap; gap: 0.75rem;
      }
      .em-cal-nav-btn {
        background: #f8fafc; border: 1.5px solid #e2e8f0;
        width: 34px; height: 34px; border-radius: 9px;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; color: #475569; transition: all 0.2s;
      }
      .em-cal-nav-btn:hover { background: #eef2ff; border-color: #4f46e5; color: #4f46e5; }
      .em-cal-month-title {
        font-size: 1.05rem; font-weight: 800;
        color: #1e293b; letter-spacing: -0.02em;
      }
      .em-cal-today-btn {
        background: #eef2ff; border: none; border-radius: 8px;
        padding: 0.35rem 0.7rem; font-size: 0.78rem; color: #4f46e5;
        font-weight: 700; cursor: pointer; transition: all 0.2s;
      }
      .em-cal-today-btn:hover { background: #4f46e5; color: #fff; }

      /* Legend */
      .em-legend { display: flex; flex-wrap: wrap; gap: 0.5rem; }
      .em-legend-item { display: flex; align-items: center; gap: 0.3rem; font-size: 0.73rem; color: #475569; font-weight: 500; }
      .em-legend-dot { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; }

      /* Calendar grid */
      .em-cal-grid {
        background: #fff; border-radius: 14px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05); overflow: hidden;
      }
      .em-cal-weekdays {
        display: grid; grid-template-columns: repeat(7, 1fr);
        background: #f8fafc; border-bottom: 1.5px solid #e2e8f0;
      }
      .em-cal-weekday {
        padding: 0.6rem 0.5rem; text-align: center;
        font-size: 0.67rem; font-weight: 800;
        text-transform: uppercase; letter-spacing: 0.06em; color: #64748b;
      }
      .em-cal-weekday.weekend { color: #94a3b8; }
      .em-cal-days { display: grid; grid-template-columns: repeat(7, 1fr); }
      .em-cal-cell {
        min-height: 108px; padding: 0.45rem 0.5rem;
        border-right: 1px solid #f1f5f9;
        border-bottom: 1px solid #f1f5f9;
        cursor: pointer; transition: background 0.15s; position: relative;
        overflow: hidden;
      }
      .em-cal-cell:hover { background: #fafbff; }
      .em-cal-cell:nth-child(7n) { border-right: none; }
      .em-cal-cell.other-month { background: #fafafa; }
      .em-cal-cell.other-month .em-day-num { color: #d1d5db; }
      .em-cal-cell.weekend { background: #fefeff; }
      .em-cal-cell.has-events { background: #fdfeff; }
      .em-day-num {
        font-size: 0.79rem; font-weight: 700; color: #334155;
        width: 24px; height: 24px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 50%; margin-bottom: 0.25rem; flex-shrink: 0;
      }
      .em-cal-cell.is-today .em-day-num {
        background: #4f46e5; color: #fff;
        box-shadow: 0 2px 8px rgba(79,70,229,0.4);
      }
      .em-event-pill {
        font-size: 0.67rem; font-weight: 600;
        padding: 0.18rem 0.45rem; border-radius: 5px;
        margin-bottom: 0.18rem; cursor: pointer;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        display: flex; align-items: center; gap: 0.22rem;
        transition: opacity 0.15s, transform 0.15s;
      }
      .em-event-pill:hover { opacity: 0.82; transform: translateX(1px); }
      .em-more-link {
        font-size: 0.67rem; color: #4f46e5; font-weight: 700;
        cursor: pointer; padding: 0.12rem 0.35rem; border-radius: 4px;
        transition: background 0.15s; display: inline-block;
      }
      .em-more-link:hover { background: #eef2ff; }

      /* Day popup */
      .em-popup {
        position: fixed; z-index: 1060;
        background: #fff; border-radius: 16px;
        box-shadow: 0 12px 48px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.06);
        min-width: 300px; max-width: 350px;
        border: 1px solid #e2e8f0; overflow: hidden;
        animation: popIn 0.18s ease;
      }
      @keyframes popIn {
        from { opacity: 0; transform: scale(0.94) translateY(-6px); }
        to   { opacity: 1; transform: scale(1)    translateY(0); }
      }
      .em-popup-header {
        background: linear-gradient(135deg, #1e293b, #0f172a);
        padding: 0.8rem 1rem;
        display: flex; align-items: center; justify-content: space-between;
      }
      .em-popup-header-title { color: #fff; font-weight: 700; font-size: 0.85rem; }
      .em-popup-close {
        background: rgba(255,255,255,0.14); border: none;
        width: 26px; height: 26px; border-radius: 7px;
        cursor: pointer; color: #fff;
        display: flex; align-items: center; justify-content: center;
        transition: background 0.2s;
      }
      .em-popup-close:hover { background: rgba(255,255,255,0.28); }
      .em-popup-body { padding: 0.65rem; max-height: 300px; overflow-y: auto; }
      .em-popup-event {
        padding: 0.6rem 0.7rem; border-radius: 10px; margin-bottom: 0.45rem;
        cursor: pointer; transition: all 0.15s;
        display: flex; align-items: flex-start; gap: 0.55rem;
        position: relative;
      }
      .em-popup-event:hover { transform: translateX(2px); }
      .em-popup-event:last-child { margin-bottom: 0; }
      .em-popup-event-dot {
        width: 9px; height: 9px; border-radius: 50%;
        flex-shrink: 0; margin-top: 4px;
      }
      .em-popup-event-title { font-size: 0.82rem; font-weight: 700; color: #1e293b; line-height: 1.3; }
      .em-popup-event-meta { font-size: 0.71rem; color: #64748b; margin-top: 0.12rem; }
      .em-popup-event-del {
        background: none; border: none; color: #ef4444; cursor: pointer;
        padding: 0.12rem 0.25rem; border-radius: 5px; flex-shrink: 0;
        opacity: 0; transition: opacity 0.15s, background 0.15s;
        font-size: 0.72rem;
      }
      .em-popup-event:hover .em-popup-event-del { opacity: 1; }
      .em-popup-event-del:hover { background: #fee2e2; }
      .em-popup-empty {
        text-align: center; padding: 1.5rem 1rem;
        color: #94a3b8; font-size: 0.8rem;
      }
      .em-popup-footer {
        padding: 0.55rem 0.75rem;
        background: #f8fafc; border-top: 1px solid #f1f5f9;
        text-align: center;
      }
      .em-popup-add-btn {
        background: none; border: none; color: #4f46e5;
        font-size: 0.79rem; font-weight: 700; cursor: pointer;
        display: inline-flex; align-items: center; gap: 0.3rem;
        padding: 0.25rem 0.5rem; border-radius: 7px; transition: background 0.15s;
      }
      .em-popup-add-btn:hover { background: #eef2ff; }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(id);
      if (el) document.head.removeChild(el);
    };
  }, []);
};

// ─── TypeBadge ────────────────────────────────────────────────────────────────

export const TypeBadge: React.FC<{ type: EventType }> = ({ type }) => {
  const c = TYPE_COLORS[type];
  return (
    <span className="em-badge" style={{ background: c.soft, color: c.bg }}>
      <i className={TYPE_ICON[type]} />
      {type}
    </span>
  );
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: string;
  type: EventType;
}> = ({ label, value, icon, type }) => {
  const c = TYPE_COLORS[type];
  return (
    <div className="col">
      <div className="em-stat-card">
        <div
          className="em-stat-icon"
          style={{ background: c.soft, color: c.bg }}
        >
          <i className={icon} />
        </div>
        <div>
          <div className="em-stat-value">{value}</div>
          <div className="em-stat-label">{label}</div>
        </div>
      </div>
    </div>
  );
};

// ___ Boutons Proteger__________________________________________________________

//  bouton protégé pour supprimer un parent
const ProtectedDeleteEvent = withPermission(
  (props: any) => (
    <button
      className="em-popup-event-del"
      title="Supprimer"
      onClick={(ev) => {
        ev.stopPropagation();
        props.onDeleteEvent(props.event);
        props.onClose();
      }}
    >
      <i className="fa fa-trash" />
    </button>
  ),
  [buildPermission(Resource.EVENT, Action.DELETE)],
  "masquer",
);

//  bouton protégé pour ajouter un parent
const ProtectedAddEventInPopup = withPermission(
  (props: any) => (
    <button
      className="em-popup-add-btn"
      onClick={() => {
        props.onCreateEvent();
        props.onClose();
      }}
    >
      <i className="fa fa-plus-circle" />
      Ajouter un événement
    </button>
  ),
  [buildPermission(Resource.EVENT, Action.CREATE)],
  "masquer",
);

//  bouton protégé pour ajouter un parent
const ProtectedAddEvent = withPermission(
  (props: any) => (
    <button className="btn btn-primary" onClick={() => props.setShowCreate(true)}>
      <i className="fa fa-plus-circle mx-2" />
      Nouvel événement
    </button>
  ),
  [buildPermission(Resource.EVENT, Action.CREATE)],
  "masquer",
);
// ─── DayEventsPopup ───────────────────────────────────────────────────────────

interface DayPopupProps {
  dayEvents: EventItem[];
  dayLabel: string;
  position: { x: number; y: number };
  onClose: () => void;
  onViewEvent: (e: EventItem) => void;
  onDeleteEvent: (e: EventItem) => void;
  onCreateEvent: () => void;
}

const DayEventsPopup: React.FC<DayPopupProps> = ({
  dayEvents,
  dayLabel,
  position,
  onClose,
  onViewEvent,
  onDeleteEvent,
  onCreateEvent,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const top = Math.min(position.y, window.innerHeight - 430);
  const left = Math.min(Math.max(8, position.x), window.innerWidth - 370);

  return (
    <div className="em-popup" style={{ top, left }} ref={ref}>
      <div className="em-popup-header">
        <span className="em-popup-header-title">
          <i className="fa fa-calendar me-2" />
          {dayLabel}
        </span>
        <button className="em-popup-close" onClick={onClose}>
          <i className="fa fa-times" />
        </button>
      </div>

      <div className="em-popup-body">
        {dayEvents.length === 0 ? (
          <div className="em-popup-empty">
            <i
              className="fa fa-calendar-times-o d-block mb-2"
              style={{ fontSize: "1.6rem" }}
            />
            Aucun événement ce jour
          </div>
        ) : (
          dayEvents.map((event) => {
            const c = TYPE_COLORS[event.type];
            return (
              <div
                key={event.id}
                className="em-popup-event"
                style={{ background: c.soft }}
                onClick={() => {
                  onViewEvent(event);
                  onClose();
                }}
              >
                <div
                  className="em-popup-event-dot"
                  style={{ background: c.bg }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="em-popup-event-title">{event.title}</div>
                  <div className="em-popup-event-meta">
                    <i className={`${TYPE_ICON[event.type]} me-1`} />
                    {event.type} · {formatDateShort(event.start_date)} →{" "}
                    {formatDateShort(event.end_date)}
                  </div>
                </div>
                <ProtectedDeleteEvent
                  event={event}
                  onDeleteEvent={onDeleteEvent}
                  onClose={onClose}
                />
              </div>
            );
          })
        )}
      </div>

      <div className="em-popup-footer">
        <ProtectedAddEventInPopup
          onCreateEvent={onCreateEvent}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

// ─── CalendarView ─────────────────────────────────────────────────────────────

interface CalendarViewProps {
  events: EventItem[];
  onViewEvent: (e: EventItem) => void;
  onDeleteEvent: (e: EventItem) => void;
  onCreateEvent: () => void;
  calYear: number;
  calMonth: number;
  setCalYear: React.Dispatch<React.SetStateAction<number>>;
  setCalMonth: React.Dispatch<React.SetStateAction<number>>;
}

interface PopupState {
  events: EventItem[];
  label: string;
  pos: { x: number; y: number };
}

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  onViewEvent,
  onDeleteEvent,
  onCreateEvent,
  calYear,
  calMonth,
  setCalYear,
  setCalMonth,
}) => {
  const today = new Date();
  const [popup, setPopup] = useState<PopupState | null>(null);

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = (() => {
    const d = new Date(calYear, calMonth, 1).getDay();
    return d === 0 ? 6 : d - 1;
  })();
  const daysInPrevMonth = new Date(calYear, calMonth, 0).getDate();

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };
  const goToday = () => {
    setCalYear(today.getFullYear());
    setCalMonth(today.getMonth());
  };

  const handleCellClick = (
    e: React.MouseEvent<HTMLDivElement>,
    year: number,
    month: number,
    day: number,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const dayEvents = getEventsForDay(events, year, month, day);
    const d = new Date(year, month, day);
    const raw = d.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    setPopup({
      events: dayEvents,
      label,
      pos: { x: rect.left, y: rect.bottom + 6 },
    });
  };

  // Build cells
  type Cell = { year: number; month: number; day: number; current: boolean };
  const cells: Cell[] = [];

  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const pm = calMonth === 0 ? 11 : calMonth - 1;
    const py = calMonth === 0 ? calYear - 1 : calYear;
    cells.push({
      year: py,
      month: pm,
      day: daysInPrevMonth - i,
      current: false,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ year: calYear, month: calMonth, day: d, current: true });
  }
  const remaining = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let d = 1; d <= remaining; d++) {
    const nm = calMonth === 11 ? 0 : calMonth + 1;
    const ny = calMonth === 11 ? calYear + 1 : calYear;
    cells.push({ year: ny, month: nm, day: d, current: false });
  }

  const MAX_PILLS = 3;

  return (
    <div>
      {/* Navigation bar */}
      <div className="em-cal-nav">
        <div className="d-flex align-items-center gap-2">
          <button
            className="em-cal-nav-btn"
            onClick={prevMonth}
            title="Mois précédent"
          >
            <i className="fa fa-chevron-left" />
          </button>
          <button
            className="em-cal-nav-btn"
            onClick={nextMonth}
            title="Mois suivant"
          >
            <i className="fa fa-chevron-right" />
          </button>
          <button className="em-cal-today-btn" onClick={goToday}>
            Aujourd'hui
          </button>
          <ConfigProvider locale={frFR}>
            <DatePicker
              picker="month"
              value={dayjs(new Date(calYear, calMonth))}
              onChange={(date) => {
                if (date) {
                  setCalYear(date.year());
                  setCalMonth(date.month());
                }
              }}
              format={(value) =>
                value
                  ? value
                      .format("MMMM YYYY")
                      .replace(/^\w/, (c) => c.toUpperCase())
                  : ""
              }
              placeholder="Sélectionner un mois"
              style={{
                minWidth: "150px",
                borderColor: "#4f46e5",
              }}
              popupClassName="em-date-picker-popup"
            />
          </ConfigProvider>
        </div>

        <span className="em-cal-month-title">
          {MONTHS_FR[calMonth]} {calYear}
        </span>

        <div className="em-legend d-none d-lg-flex">
          {EVENT_TYPES.map((t) => (
            <div key={t} className="em-legend-item">
              <div
                className="em-legend-dot"
                style={{ background: TYPE_COLORS[t].bg }}
              />
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="em-cal-grid">
        {/* Weekday headers */}
        <div className="em-cal-weekdays">
          {DAYS_FR.map((d, i) => (
            <div
              key={d}
              className={`em-cal-weekday${i >= 5 ? " weekend" : ""}`}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="em-cal-days">
          {cells.map((cell, idx) => {
            const isToday =
              cell.current &&
              cell.day === today.getDate() &&
              cell.month === today.getMonth() &&
              cell.year === today.getFullYear();
            const isWeekend = idx % 7 >= 5;
            const dayEvts = getEventsForDay(
              events,
              cell.year,
              cell.month,
              cell.day,
            );
            const visible = dayEvts.slice(0, MAX_PILLS);
            const hidden = dayEvts.length - MAX_PILLS;

            let cls = "em-cal-cell";
            if (!cell.current) cls += " other-month";
            if (isToday) cls += " is-today";
            if (isWeekend) cls += " weekend";
            if (dayEvts.length > 0) cls += " has-events";

            return (
              <div
                key={idx}
                className={cls}
                onClick={(e) =>
                  handleCellClick(e, cell.year, cell.month, cell.day)
                }
              >
                <div className="em-day-num">{cell.day}</div>

                {visible.map((event) => {
                  const c = TYPE_COLORS[event.type];
                  return (
                    <div
                      key={event.id}
                      className="em-event-pill"
                      style={{ background: c.bg, color: c.text }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewEvent(event);
                      }}
                      title={event.title}
                    >
                      <i
                        className={TYPE_ICON[event.type]}
                        style={{ fontSize: "0.58rem", flexShrink: 0 }}
                      />
                      <span
                        style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {event.title}
                      </span>
                    </div>
                  );
                })}

                {hidden > 0 && (
                  <div className="em-more-link">
                    +{hidden} autre{hidden > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Popup */}
      {popup && (
        <DayEventsPopup
          dayEvents={popup.events}
          dayLabel={popup.label}
          position={popup.pos}
          onClose={() => setPopup(null)}
          onViewEvent={onViewEvent}
          onDeleteEvent={onDeleteEvent}
          onCreateEvent={onCreateEvent}
        />
      )}
    </div>
  );
};

// ─── EventsManager ────────────────────────────────────────────────────────────

const EventsManager = () => {
  const routes = my_routes;
  const { selectedYear } = useAcademicYear();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [classes, setClasses] = useState<ClassOption[]>([]);

  const [filterType, setFilterType] = useState<EventType | "">("");
  const [searchText, setSearchText] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [viewEvent, setViewEvent] = useState<EventItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventItem | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editEvent, setEditEvent] = useState<EventItem | null>(null);

  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  useStyles();

  // ── Aggregate events by ID ─────────────────────────────────────────────────

  const aggregateEventsByIdAndClasses = useCallback(
    (events: any[]): EventItem[] => {
      const groupedById: Record<string, EventItem> = {};

      events.forEach((event) => {
        if (!groupedById[event.id]) {
          // Premier événement avec cet ID
          groupedById[event.id] = {
            ...event,
            Classes: event.all_school
              ? []
              : event.id_class?.map((id: string) => {
                  const matchedClass = classes.find((c) => c.id === id);

                  return matchedClass;
                }),
          };
        } else {
          // Événement suivant avec le même ID - on ajoute les classes
          if (!event.all_school && event.Class) {
            const classId = event.Class.id;
            // Vérifier si la classe n'existe pas déjà
            if (!groupedById[event.id].Classes?.find((c) => c.id === classId)) {
              groupedById[event.id].Classes?.push({
                id: event.Class.id,
                name: `${event.Class?.Level?.abbreviation} - ${event.Class.label}`,
              });
            }
          }
        }
      });

      return Object.values(groupedById);
    },
    [],
  );

  // ── Load events ────────────────────────────────────────────────────────────

  const loadEvents = useCallback(async () => {
    if (!selectedYear?.id) return;
    setLoading(true);
    setFetchError("");
    try {
      const res = await getEventByYear(selectedYear.id);
      const aggregatedEvents = aggregateEventsByIdAndClasses(res.data ?? []);
      // console.log("Événements chargés: ", res.data);
      setEvents(aggregatedEvents);
    } catch (error: any) {
      // console.log("Erreur lors du chargement des événements: ", error);
      setFetchError(
        error?.response?.data?.message ??
          "Impossible de charger les événements.",
      );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produite, réessayez plus tard",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedYear, aggregateEventsByIdAndClasses]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // ── Load classes ───────────────────────────────────────────────────────────

  const fetchAllClass = async () => {
    try {
      if (!selectedYear?.id) {
        setClasses([]);
        return;
      }
      const response = await getAllClass(
        selectedYear.id,
        buildPermissionContext(Resource.EVENT, Action.READ),
      );
      setClasses(
        response.data
          .slice()
          .sort((a: any, b: any) => {
            const lc = a.Level.label.localeCompare(b.Level.label, "fr", {
              numeric: true,
            });
            if (lc !== 0) return lc;
            return a.label.localeCompare(b.label, "fr", { numeric: true });
          })
          .map((c: any) => ({
            id: c.id,
            name: `${c?.Level?.abbreviation} - ${c?.label}`,
          })),
      );
    } catch (error: any) {
      // console.log("Erreur récupération des classes: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produite, réessayez plus tard",
      );
    }
  };

  useEffect(() => {
    fetchAllClass();
  }, []);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    try {
      if (!deleteTarget) return;
      await deleteEvent(deleteTarget.id);
      setDeleteTarget(null);
      await loadEvents();
      toast.success("Suppression effectuee avec succes !");
    } catch (error: any) {
      // console.log("Erreur lors de la suppression de l'événement: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur lors de la suppression de l'événement, réessayez plus tard",
      );
    }
  };

  // ── Filters ────────────────────────────────────────────────────────────────

  const filtered = events.filter((e) => {
    const matchType = filterType === "" || e.type === filterType;
    const matchText =
      searchText.trim() === "" ||
      e.title.toLowerCase().includes(searchText.toLowerCase()) ||
      (e.description ?? "").toLowerCase().includes(searchText.toLowerCase());
    return matchType && matchText;
  });

  const resetFilters = () => {
    setFilterType("");
    setSearchText("");
  };

  const countByType = (t: EventType) =>
    events.filter((e) => e.type === t).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="page-wrapper">
      <div className="content">
        {/*<div className="em-page">*/}
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Gestion des événements</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.timeTableList}>EDT</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Evénements
                </li>
              </ol>
            </nav>
          </div>
          {/* Boutton en entête de la page */}
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <ProtectedAddEvent setShowCreate={setShowCreate} />
          </div>
        </div>

        <div className="container-fluid ">
          {/* Stats */}
          {events.length > 0 && (
            <div className="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3 mb-4">
              <StatCard
                label="Congés"
                value={countByType("Congé")}
                icon="fa fa-umbrella"
                type="Congé"
              />
              <StatCard
                label="Jours fériés"
                value={countByType("Jour Férié")}
                icon="fa fa-star"
                type="Jour Férié"
              />
              <StatCard
                label="Examens"
                value={countByType("Examen")}
                icon="fa fa-pencil-square"
                type="Examen"
              />
              <StatCard
                label="Activités"
                value={countByType("Activité Scolaire")}
                icon="fa fa-person"
                type="Activité Scolaire"
              />
              <StatCard
                label="Devoirs"
                value={countByType("Dévoir")}
                icon="fa fa-book"
                type="Dévoir"
              />
            </div>
          )}

          {/* Toolbar */}
          <div className="em-toolbar">
            <div className="em-search-wrap">
              <i className="fa fa-search" />
              <input
                type="text"
                className="em-search-input"
                placeholder="Rechercher un événement…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <select
              className="em-type-filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as EventType | "")}
            >
              <option value="">Tous les types</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {(filterType || searchText) && (
              <button className="em-btn-reset" onClick={resetFilters}>
                <i className="fa fa-times" />
                Réinitialiser
              </button>
            )}
            {(filterType || searchText) && (
              <span
                style={{
                  fontSize: "0.78rem",
                  color: "#4f46e5",
                  fontWeight: 600,
                  background: "#eef2ff",
                  padding: "0.3rem 0.7rem",
                  borderRadius: 8,
                  whiteSpace: "nowrap",
                }}
              >
                {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Calendar or states */}
          {loading ? (
            <div className="em-loading">
              <div className="em-spinner" />
              <span style={{ fontSize: "0.9rem" }}>
                Chargement des événements…
              </span>
            </div>
          ) : fetchError ? (
            <div className="em-empty">
              <i
                className="fa fa-wifi em-empty-icon"
                style={{ color: "#fca5a5" }}
              />
              <p
                style={{
                  color: "#ef4444",
                  fontWeight: 700,
                  marginBottom: "0.5rem",
                }}
              >
                {fetchError}
              </p>
              <button className="em-btn em-btn-primary" onClick={loadEvents}>
                <i className="fa fa-refresh" />
                Réessayer
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="em-empty flex-column gap-3 align-items-center justify-content-center w-100 text-center">
              <i className="fa fa-calendar-times em-empty-icon" />
              <p
                style={{
                  color: "#475569",
                  fontWeight: 600,
                  marginBottom: "0.5rem",
                }}
              >
                Aucun événement enregistré pour cette année académique
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreate(true)}
              >
                <i className="fa fa-plus-circle me-2" />
                Créer le premier événement
              </button>
            </div>
          ) : (
            <CalendarView
              events={filtered}
              onViewEvent={setViewEvent}
              onDeleteEvent={setDeleteTarget}
              onCreateEvent={() => setShowCreate(true)}
              calYear={calYear}
              calMonth={calMonth}
              setCalYear={setCalYear}
              setCalMonth={setCalMonth}
            />
          )}
        </div>

        {/* Modals */}
        <CreateEventModal
          show={showCreate}
          availableClasses={classes}
          onClose={() => setShowCreate(false)}
          onSuccess={loadEvents}
        />

        <EditEventModal
          show={showEdit}
          event={editEvent}
          availableClasses={classes}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false);
            loadEvents();
            setViewEvent(null);
          }}
        />

        <ViewEventModal
          event={viewEvent}
          onClose={() => setViewEvent(null)}
          onEdit={(e) => {
            setEditEvent(e);
            setShowEdit(true);
          }}
          onDelete={(e) => {
            setDeleteTarget(e);
            setViewEvent(null);
          }}
        />

        <DeleteConfirmModal
          event={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
        {/*</div>*/}
      </div>
    </div>
  );
};

export default EventsManager;
